import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Calendar, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Sermons = () => {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSermons = async () => {
      try {
        const response = await axios.get(`${API}/sermons`);
        setSermons(response.data);
      } catch {
        // Silently fall back to defaultSermons on error
      } finally {
        setLoading(false);
      }
    };
    fetchSermons();
  }, []);

  const defaultSermons = [
    {
      id: '1',
      title: 'The Power of Faith',
      speaker: 'Pastor Dwayne Browne',
      date: 'January 26, 2025',
      description: 'Discover how faith can move mountains and transform your life through the power of God.',
      thumbnail: 'https://images.unsplash.com/photo-1643988613067-fb37d4ffc606?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '2',
      title: 'Walking in Purpose',
      speaker: 'Apostle Kenneth Browne',
      date: 'January 19, 2025',
      description: 'Understanding God\'s divine purpose for your life and how to walk in it daily.',
      thumbnail: 'https://images.unsplash.com/photo-1643988613067-fb37d4ffc606?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '3',
      title: 'Empowered to Serve',
      speaker: 'Pastor Dwayne Browne',
      date: 'January 12, 2025',
      description: 'How the Holy Spirit empowers us to serve God and impact our communities.',
      thumbnail: 'https://images.unsplash.com/photo-1643988613067-fb37d4ffc606?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '4',
      title: 'Love Without Limits',
      speaker: 'Apostle Yolanda Browne',
      date: 'January 5, 2025',
      description: 'Exploring the boundless love of God and how it transforms our relationships.',
      thumbnail: 'https://images.unsplash.com/photo-1643988613067-fb37d4ffc606?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '5',
      title: 'Building Strong Foundations',
      speaker: 'Pastor Dwayne Browne',
      date: 'December 29, 2024',
      description: 'Establishing your life on the solid rock of God\'s Word and promises.',
      thumbnail: 'https://images.unsplash.com/photo-1643988613067-fb37d4ffc606?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      id: '6',
      title: 'The Fire Within',
      speaker: 'Apostle Kenneth Browne',
      date: 'December 22, 2024',
      description: 'Keeping the fire of God burning bright in your heart and ministry.',
      thumbnail: 'https://images.unsplash.com/photo-1643988613067-fb37d4ffc606?crop=entropy&cs=srgb&fm=jpg&q=85'
    }
  ];

  const displaySermons = sermons.length > 0 ? sermons : defaultSermons;

  return (
    <div data-testid="sermons-page" className="min-h-screen">
      <section 
        className="py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 50%, #CD853F 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '600px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute bottom-20 right-20 text-white/5 transform -rotate-12">
          <svg width="200" height="200" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-amber-200">Messages</span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mt-4 drop-shadow-lg">
              Sermon Library
            </h1>
            <p className="text-xl text-white/95 mt-6 max-w-3xl mx-auto">
              Watch and listen to powerful messages that will inspire, encourage, and strengthen your faith.
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <p className="mt-4 text-white/90">Loading sermons...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displaySermons.map((sermon, index) => (
                <motion.div
                  key={sermon.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`sermon-card-${index}`}
                  className="group bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={sermon.thumbnail}
                      alt={sermon.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button
                          data-testid={`play-sermon-${index}`}
                          className="w-16 h-16 bg-white/95 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-2xl"
                        >
                          <Play className="w-8 h-8 text-primary ml-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-3">
                    <h3 className="text-xl font-heading font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                      {sermon.title}
                    </h3>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{sermon.speaker}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{sermon.date}</span>
                      </div>
                    </div>
                    
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {sermon.description}
                    </p>
                    
                    <button
                      data-testid={`watch-sermon-${index}`}
                      className="w-full bg-gradient-to-r from-primary to-amber-600 text-white py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 mt-4"
                    >
                      Watch Sermon
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
